import React from 'react';
import styled from '@benzinga/themetron';
import { Icon } from '@benzinga/core-ui';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { faSpotify } from '@fortawesome/free-brands-svg-icons/faSpotify';
import classNames from 'classnames';

type PodcastPlatformBadgesVariant = 'default' | 'pill' | 'rectangle' | 'icons';

export interface PodcastPlatformBadgesProps {
  className?: string;
  variant?: PodcastPlatformBadgesVariant;
  hideName?: boolean;
  platforms: {
    [platform: string]: {
      name: string;
      url: string | null;
    };
  };
}

interface PodcastPlatorm {
  icon: JSX.Element;
  name: string;
  url: string;
  color?: string;
  id: string;
}

export const PodcastPlatformBadges: React.FC<PodcastPlatformBadgesProps> = ({
  className,
  hideName = false,
  platforms,
  variant = 'default',
}) => {
  const podcastPlatforms: PodcastPlatorm[] = [];

  const availablePodcastPlatforms: PodcastPlatorm[] = [
    {
      color: '#1DB954',
      icon: <Icon icon={faSpotify} />,
      id: 'spotify',
      name: 'Spotify',
      url: 'https://open.spotify.com/show/6fvsnVzjAOP4PMQWS3Mf3z',
    },
    {
      color: '#EB080B',
      icon: <Icon icon={faYoutube} />,
      id: 'youtube',
      name: 'YouTube',
      url: 'https://www.youtube.com/playlist?list=PL4k0fH8EgI3aKwWI3oZZ1cDyPkZv_agYN',
    },
    {
      color: '#832bc1',
      icon:
        variant === 'default' ? (
          <svg aria-label="iTunes" role="presentation" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
            <rect fill="rgba(255,255,255,0)" height="100%" width="100%"></rect>
            <g>
              <path
                d="M51.21 53.652c-.089-.803-.356-1.382-.891-1.918-1.003-1.048-2.763-1.739-4.835-1.739s-3.832.67-4.835 1.74c-.512.557-.802 1.114-.89 1.917-.179 1.561-.068 2.9.11 5.062.179 2.052.513 4.795.936 7.56.312 1.985.557 3.055.78 3.813.379 1.25 1.738 2.32 3.9 2.32 2.16 0 3.542-1.093 3.898-2.32.223-.758.468-1.828.78-3.813.423-2.788.758-5.508.936-7.56.2-2.163.29-3.5.111-5.062z"
                transform="translate(512.874 486.402) scale(18.1832) translate(-.513 15.2) translate(-45.487 -61.2)"
              ></path>
              <path
                d="M50.962 42.279c0 3.032-2.451 5.485-5.481 5.485S40 45.311 40 42.28c0-3.033 2.45-5.486 5.48-5.486s5.482 2.475 5.482 5.486z"
                transform="translate(512.874 486.402) scale(18.1832) translate(-.52 -3.721) translate(-45.48 -42.279)"
              ></path>
              <path
                d="M45.417 20.67c-12.789.045-23.26 10.436-23.438 23.236-.134 10.37 6.483 19.245 15.73 22.5.222.09.445-.11.423-.334-.112-.802-.245-1.605-.335-2.408a.884.884 0 00-.468-.647c-7.307-3.189-12.41-10.525-12.32-19.021.111-11.15 9.201-20.226 20.319-20.316 11.385-.089 20.676 9.143 20.676 20.516 0 8.407-5.08 15.633-12.32 18.821a.757.757 0 00-.469.647l-.334 2.408c-.045.246.2.424.423.335 9.157-3.211 15.73-11.953 15.73-22.21-.045-12.98-10.628-23.55-23.617-23.527z"
                transform="translate(512.874 486.402) scale(18.1832) translate(-.495 -2.45) translate(-45.505 -43.55)"
              ></path>
              <path
                d="M44.88 28.475c-8.333.312-15.017 7.202-15.129 15.543-.067 5.485 2.674 10.347 6.885 13.223.2.134.49-.022.49-.267a21.89 21.89 0 01-.022-2.698.909.909 0 00-.312-.759 12.757 12.757 0 01-4.033-9.633c.178-6.69 5.548-12.131 12.232-12.399 7.263-.29 13.234 5.553 13.234 12.733 0 3.657-1.56 6.958-4.033 9.3-.2.2-.311.468-.311.757.044.87.022 1.718-.023 2.677-.022.245.268.423.49.267 4.144-2.832 6.885-7.626 6.885-13.023.022-8.875-7.397-16.078-16.354-15.721z"
                transform="translate(512.874 486.402) scale(18.1832) translate(-.508 -3.124) translate(-45.492 -42.876)"
              ></path>
            </g>
          </svg>
        ) : (
          <svg aria-label="iTunes" role="presentation" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
            <rect fill="url(#t)" height="512" rx="15%" width="512"></rect>
            <defs>
              <linearGradient id="t" x2="0" y1="100%">
                <stop offset="0" stopColor="#832bc1"></stop>
                <stop offset="1" stopColor="#f452ff"></stop>
              </linearGradient>
            </defs>
            <path
              d="M293 294c-8-8-21-13-37-13s-29 5-37 13c-4 5-6 9-7 15-1 12 0 22 1 39a891 891 0 0013 87c3 9 14 18 30 18 17 0 27-9 30-18a891 891 0 0014-126c-1-6-3-10-7-15zm-79-72a42 42 0 1084 0 42 42 0 00-84 0zm42-165a180 180 0 00-60 350c2 0 4-1 4-3l-3-18c0-3-2-4-4-5a157 157 0 11126 0c-2 1-4 2-4 5l-3 18c0 2 2 3 4 3a180 180 0 00-60-350zm-4 82a98 98 0 0171 169c-2 1-3 4-2 6l-1 20c0 2 2 4 4 2a121 121 0 10-136 1c2 1 4-1 4-3v-20c0-2-1-5-3-6a97 97 0 0163-169z"
              fill="#fff"
            ></path>
          </svg>
        ),
      id: 'apple-podcasts',
      name: 'Apple Podcasts',
      url: 'https://podcasts.apple.com/us/podcast/the-razreport/id1543761049',
    },
    {
      color: '#ec8b24',
      icon: (
        <svg aria-label="Stitcher" role="presentation" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
          <path d="M56 205h70v127H56z" fill="#3590c5"></path>
          <path d="M386 198h70v117h-70z" fill="#c53827"></path>
          <path d="M304 172h70v152h-70z" fill="#ec8b24"></path>
          <path d="M222 194h70v146h-70z" fill="#fbc618"></path>
          <path d="M139 182h70v141h-70z" fill="#612268"></path>
        </svg>
      ),
      id: 'stitcher',
      name: 'Stitcher',
      url: 'https://www.stitcher.com/podcast/benzinga-morning-show',
    },
    {
      color: '#14D8CC',
      icon: (
        <svg aria-label="TuneIn" role="presentation" viewBox="0 0 201 88">
          <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
            <g fill="currentColor">
              <path d="M180.681 20.8c0-.441-.36-.8-.799-.8h-4c-.44 0-.8.359-.8.8v15.037c0 .24-.28.32-.36.08l-5.479-15.197c-.12-.361-.4-.72-1-.72h-4.759c-.439 0-.8.359-.8.8v26.395c0 .44.361.8.8.8h4c.44 0 .8-.36.8-.8V33.357c0-.24.28-.319.36-.08l5.44 14.038c.118.32.398.68 1.078.68h4.72c.44 0 .8-.36.8-.8V20.8zm-22.439 22.595a.801.801 0 00-.8-.799h-3.399c-.24 0-.4-.159-.4-.4V25.799c0-.24.16-.401.4-.401h3a.801.801 0 00.8-.799V20.8c0-.441-.36-.8-.8-.8h-12.398a.803.803 0 00-.8.8v3.799c0 .44.36.799.8.799h3c.24 0 .4.161.4.401v16.397c0 .241-.16.4-.4.4h-3.4c-.44 0-.8.36-.8.799v3.8c0 .44.36.8.8.8h13.197c.441 0 .8-.36.8-.8v-3.8zM122.445 26H6.945a.5.5 0 00-.5.5v55a.5.5 0 00.5.5h115a.5.5 0 00.5-.5V26zm78-23v62a3 3 0 01-3 3h-69v17a3 3 0 01-3 3h-122a3 3 0 01-3-3V23a3 3 0 013-3h119V3a3 3 0 013-3h72a3 3 0 013 3zM104.72 62.396h-9.478c-.24 0-.4-.16-.4-.4v-4.799c0-.24.16-.4.4-.4h8.998c.44 0 .8-.359.8-.8v-3.999c0-.44-.36-.8-.8-.8h-8.998c-.24 0-.4-.16-.4-.4v-4.799c0-.24.16-.4.4-.4h9.318c.44 0 .8-.36.8-.8V40.8c0-.441-.36-.8-.8-.8H90.043c-.44 0-.8.359-.8.8v26.395c0 .44.36.8.8.8h14.677c.44 0 .8-.36.8-.8v-3.999c0-.44-.36-.8-.8-.8zM84.001 40h-4c-.44 0-.8.359-.8.8v15.037c0 .24-.279.32-.36.08L73.362 40.72c-.12-.361-.399-.72-1-.72h-4.759c-.439 0-.8.359-.8.8v26.395c0 .44.361.8.8.8h4c.44 0 .8-.36.8-.8V53.357c0-.24.28-.319.36-.08l5.44 14.038c.118.32.398.68 1.078.68h4.72c.44 0 .8-.36.8-.8V40.8c0-.441-.36-.8-.8-.8zm-22.56 0h-4.039c-.44 0-.8.359-.8.8v18.797c0 1.759-1.439 3.199-3.88 3.199-2.359 0-3.798-1.44-3.798-3.199V40.8c0-.441-.36-.8-.8-.8h-4.039a.802.802 0 00-.8.8v18.797c0 4.839 4 8.838 9.437 8.838 5.52 0 9.52-3.999 9.52-8.838V40.8c0-.441-.36-.8-.8-.8zm-21.999.8v3.999c0 .44-.36.8-.8.8h-4.999c-.24 0-.4.16-.4.4v21.196c0 .44-.36.8-.8.8h-3.999c-.44 0-.8-.36-.8-.8V45.999c0-.24-.16-.4-.4-.4h-4.999c-.44 0-.8-.36-.8-.8V40.8c0-.441.36-.8.8-.8h16.397c.44 0 .8.359.8.8z"></path>
            </g>
          </g>
        </svg>
      ),
      id: 'tunein',
      name: 'TuneIn',
      url: 'https://tunein.com/podcasts/Business--Economics-Podcasts/PreMarket-Prep-p1122264',
    },
    {
      color: '#BC1E29',
      icon: (
        <svg
          aria-label="Podbean"
          fill="none"
          height="26"
          role="presentation"
          viewBox="0 0 27 26"
          width="27"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_24595_86527)">
            <path
              d="M13.2611 0C13.1335 0.0040625 13.0109 0.0089375 12.8841 0.013C12.5331 0.0333125 12.0497 0.0804375 11.6613 0.130812C8.09848 0.6175 5.05486 2.40825 3.0033 5.07406C2.49548 5.72975 2.05511 6.43256 1.67811 7.17275C1.61555 7.30356 1.55136 7.43031 1.49205 7.56194C1.39455 7.77888 1.29786 7.99419 1.2093 8.21437C1.09473 8.49794 0.992359 8.78069 0.899734 9.06913L0.850172 9.22594C0.477234 10.4447 0.291172 11.7098 0.287109 12.9829V13.0252C0.300109 20.1947 6.11842 26 13.2871 26C19.9602 26 25.5461 20.9471 26.2197 14.3122L26.2229 14.287C26.2652 13.8637 26.2871 13.4396 26.2871 13.0219V12.9667C26.283 11.4221 25.9987 9.88569 25.4527 8.43862C25.4104 8.32487 25.3641 8.21031 25.317 8.10063C25.2414 7.91456 25.161 7.73256 25.0724 7.55056C23.4645 4.13481 20.4176 1.495 16.7362 0.5005C15.9107 0.27625 14.5904 0.0771875 13.6844 0.02275C13.5617 0.0138125 13.4399 0.0056875 13.3115 0.0008125L13.2611 0ZM15.775 3.11919C15.957 3.12325 16.1853 3.18338 16.507 3.29225C18.4367 3.952 20.5947 5.62819 21.7664 7.37181C22.5789 8.58162 23.2134 9.99862 23.2134 10.6047C23.2134 10.9939 22.8364 11.3457 22.4302 11.3457C22.0499 11.3457 21.6599 11.011 21.5592 10.6047C21.4194 10.0384 20.6581 8.5995 20.0991 7.86256C19.1087 6.55525 17.5771 5.41206 16.0699 4.85306C15.0129 4.45575 14.7374 3.991 15.2282 3.40681C15.3931 3.20775 15.541 3.11838 15.7742 3.11838L15.775 3.11919ZM11.5638 4.63775C11.8474 4.63775 12.1009 4.69706 12.3251 4.81569C13.7429 5.53475 13.6674 7.59687 12.1894 8.16319C10.3662 8.85787 9.44723 9.59319 8.85086 10.8046C7.60286 13.3388 8.62255 16.3304 11.1486 17.5411C13.0068 18.4307 15.1225 18.122 16.5997 16.7586C17.2724 16.1322 17.6405 15.5951 17.9704 14.7233C18.4359 13.5046 18.9649 13.0471 19.913 13.0471C21.1229 13.0471 21.9573 14.0709 21.7111 15.2433C21.2074 17.55 19.2695 19.8689 16.9206 20.9568C15.6263 21.554 14.9154 21.7051 13.2774 21.7051C11.6694 21.7051 10.9707 21.5613 9.72267 20.9942C6.31586 19.4537 4.22611 15.7641 4.65673 12.103C5.06705 8.71731 7.39405 5.87844 10.6278 4.80756C10.9699 4.693 11.2835 4.63287 11.563 4.63775H11.5638ZM15.3175 5.6745C15.5548 5.67856 15.8595 5.78906 16.308 6.00925C18.1369 6.877 19.6465 8.476 20.459 10.3805C20.7637 11.1085 20.6118 11.5822 20.0195 11.7683C19.562 11.9039 19.2582 11.6586 18.8633 10.8331C18.1231 9.27225 17.0734 8.19162 15.597 7.46525C14.7382 7.05006 14.6318 6.95256 14.5473 6.54631C14.5091 6.331 14.5473 6.2075 14.7464 5.97106C14.9154 5.772 15.0803 5.66963 15.3175 5.6745ZM14.8861 8.32406C15.1355 8.32406 15.4232 8.43781 15.7953 8.684C16.8361 9.36569 18.0085 10.8331 18.0085 11.4465C18.0085 11.7593 17.7087 12.1582 17.404 12.2338C16.9978 12.3403 16.7053 12.1363 16.2787 11.4676C15.8172 10.7567 15.2753 10.2196 14.7163 9.93606C14.2345 9.68581 14.0947 9.50056 14.0947 9.10162C14.0777 8.88144 14.1792 8.67019 14.3572 8.54344C14.501 8.40369 14.6919 8.32812 14.8869 8.32325L14.8861 8.32406Z"
              fill={variant === 'default' ? '#FFFFFF' : '#BC1E29'}
            />
          </g>
          <defs>
            <clipPath id="clip0_24595_86527">
              <rect fill="white" height="26" transform="translate(0.285156)" width="26" />
            </clipPath>
          </defs>
        </svg>
      ),
      id: 'podbean',
      name: 'Podbean',
      url: 'https://www.podbean.com/podcast-detail/nb6dh-610e6/PreMarket-Prep-Podcast',
    },
    {
      color: '#F9BA03',
      icon: (
        <svg
          aria-label="Google Podcasts"
          data-name="Layer 1"
          height="26"
          role="presentation"
          viewBox="0 0 32 32"
          width="26"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M16 29a1.5 1.5 0 01-1.5-1.5v-2a1.5 1.5 0 013 0v2A1.5 1.5 0 0116 29z" fill="#ffba00"></path>
          <path d="M4.5 18.5A1.5 1.5 0 013 17v-2a1.5 1.5 0 013 0v2a1.5 1.5 0 01-1.5 1.5z" fill="#0066da"></path>
          <path d="M27.5 18.5A1.5 1.5 0 0126 17v-2a1.5 1.5 0 013 0v2a1.5 1.5 0 01-1.5 1.5z" fill="#4285f4"></path>
          <path d="M16 8a1.5 1.5 0 01-1.5-1.5v-2a1.5 1.5 0 013 0v2A1.5 1.5 0 0116 8z" fill="#ffba00"></path>
          <path
            d="M10.25 24.5a1.5 1.5 0 01-1.5-1.5v-2a1.5 1.5 0 013 0v2a1.5 1.5 0 01-1.5 1.5zM10.25 17a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 013 0v6a1.5 1.5 0 01-1.5 1.5z"
            fill="#ea4435"
          ></path>
          <path d="M16 22a1.5 1.5 0 01-1.5-1.5v-9a1.5 1.5 0 013 0v9A1.5 1.5 0 0116 22z" fill="#ffba00"></path>
          <path
            d="M21.75 12.75a1.5 1.5 0 01-1.5-1.5v-2a1.5 1.5 0 013 0v2a1.5 1.5 0 01-1.5 1.5zM21.75 24.25a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 013 0v6a1.5 1.5 0 01-1.5 1.5z"
            fill="#00ac47"
          ></path>
        </svg>
      ),
      id: 'google-podcasts',
      name: 'Podcasts',
      url: 'https://podcasts.google.com/?feed=aHR0cHM6Ly9mZWVkcy5zb3VuZGNsb3VkLmNvbS91c2Vycy9zb3VuZGNsb3VkOnVzZXJzOjEwOTEwMDEzNy9zb3VuZHMucnNz',
    },
  ];

  availablePodcastPlatforms.forEach(availablePodcastPlatform => {
    const platform = platforms[availablePodcastPlatform.id];
    if (platform?.url && platform.url !== '#') {
      podcastPlatforms.push({ ...availablePodcastPlatform, name: platform.name, url: platform.url });
    }
  });

  if (podcastPlatforms.length === 0) return null;

  if (variant === 'rectangle') {
    return (
      <PodcastPlatformsContainer
        className={classNames('podcast-platforms', { [`${className}`]: !!className, variant })}
      >
        {podcastPlatforms.map(platform => (
          <PodcastPlatformBadge
            $borderColor={platform.color}
            $color={platform.color}
            aria-label={`Listen on ${platform.name}`}
            className={classNames('podcast-platform-badge flex items-center', { [`${variant}`]: variant })}
            href={platform.url}
            key={`${platform.name}-${platform.id}`}
            rel="noreferrer noopener"
            target="_blank"
          >
            <div className="podcast-platform-badge-icon">{platform.icon}</div>
            {!hideName && (
              <div className="flex flex-col">
                <span className="platform-name font-bold text-gray-800">{platform.name}</span>
              </div>
            )}
          </PodcastPlatformBadge>
        ))}
      </PodcastPlatformsContainer>
    );
  } else if (variant === 'icons') {
    return (
      <PodcastPlatformsContainer
        className={classNames('podcast-platforms', { [`${className}`]: !!className, variant })}
      >
        {podcastPlatforms.map(platform => (
          <PodcastPlatformBadge
            $color={platform.color}
            aria-label={`Listen on ${platform.name}`}
            className={classNames('podcast-platform-badge', {
              [`${variant}`]: variant,
            })}
            href={platform.url}
            key={`${platform.name}-${platform.id}`}
            rel="noreferrer noopener"
            target="_blank"
          >
            <div className="podcast-platform-badge-icon">{platform.icon}</div>
          </PodcastPlatformBadge>
        ))}
      </PodcastPlatformsContainer>
    );
  } else if (variant === 'pill') {
    return (
      <PodcastPlatformsContainer
        className={classNames('podcast-platforms', { [`${className}`]: !!className, variant })}
      >
        {podcastPlatforms.map(platform => (
          <PodcastPlatformBadge
            $color={platform.color}
            aria-label={`Listen on ${platform.name}`}
            className={classNames('podcast-platform-badge flex items-center', { [`${variant}`]: variant })}
            href={platform.url}
            key={`${platform.name}-${platform.id}`}
            rel="noreferrer noopener"
            target="_blank"
          >
            <div className="podcast-platform-badge-icon">{platform.icon}</div>
            <div className="flex flex-col">
              <span className="listen-on-text">Listen on</span>
              {!hideName && <span className="platform-name font-bold text-white uppercase">{platform.name}</span>}
            </div>
          </PodcastPlatformBadge>
        ))}
      </PodcastPlatformsContainer>
    );
  }

  return (
    <PodcastPlatformsContainer
      className={classNames('podcast-platforms', { [`${className}`]: !!className, [`${variant}`]: variant })}
    >
      {podcastPlatforms.map(platform => (
        <PodcastPlatformBadge
          $color={platform.color}
          aria-label={`Listen on ${platform.name}`}
          className={classNames('podcast-platform-badge flex items-center', { [`${variant}`]: variant })}
          href={platform.url}
          key={`${platform.name}-${platform.id}`}
          rel="noreferrer noopener"
          target="_blank"
        >
          <div className="podcast-platform-badge-icon">{platform.icon}</div>
          {!hideName && (
            <div className="flex flex-col">
              <span className="platform-name font-bold text-gray-800">{platform.name}</span>
            </div>
          )}
        </PodcastPlatformBadge>
      ))}
    </PodcastPlatformsContainer>
  );
};

const PodcastPlatformsContainer = styled.div`
  &.podcast-platforms {
    display: flex;
    flex-wrap: wrap;
    overflow-x: auto;
    gap: 1rem;
  }
`;

const PodcastPlatformBadge = styled.a<{ $borderColor?: string; $color?: string }>`
  background-color: ${({ $color }) => ($color ? `${$color}40` : 'unset')};
  backdrop-filter: blur(6px);
  transition: background-color 0.3s ease;
  cursor: pointer;
  ${({ $borderColor }) => ($borderColor ? `border: 1px solid ${$borderColor}` : null)};

  &:hover {
    background-color: ${({ $color }) => ($color ? `${$color}60` : 'unset')};
  }

  .listen-on-text {
    color: ${({ theme }) => theme.colorPalette.gray300};
    font-size: 14px;
  }

  &.default,
  &.rectangle {
    border-radius: ${({ theme }) => theme.borderRadius.default};
    padding-right: 1.75rem;
    padding-left: 0.25rem;
  }

  &.default {
    background-color: ${({ $color }) => $color};
    border: none;
    padding: 0.45rem 1.4rem;
    border-radius: ${({ theme }) => theme.borderRadius.md};

    .platform-name {
      color: #ffffff;
      font-weight: 500;
    }

    .podcast-platform-badge-icon {
      width: 26px;
      height: 26px;
      svg {
        width: 22px !important;
        height: 22px !important;

        path {
          fill: #ffffff !important;
        }
      }
    }
  }

  &.pill {
    border-radius: 2.5rem;
    padding: 0.5rem 1rem;
    padding-left: 0.45rem;
    min-width: 190px;
    background: #3a5072;

    .podcast-platform-badge-icon {
      background: #192940;
      border-radius: ${({ theme }) => theme.borderRadius.full};
      svg {
        width: 24px !important;
        height: 24px !important;
      }
    }
  }

  &.icons {
    background-color: transparent;
    .podcast-platform-badge-icon {
      margin-right: 0;
    }
  }

  .podcast-platform-badge-icon {
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.5rem;
    color: ${({ $color }) => $color};
    svg {
      width: 30px !important;
      height: 30px !important;
    }
  }
`;
